import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SportsBasketballRoundedIcon from "@mui/icons-material/SportsBasketballRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { useNavigate, useParams } from "react-router-dom";

const statusMap = {
  live: {
    label: "EN VIVO",
    bg: "rgba(211, 47, 47, 0.10)",
    color: "error.main",
  },
  scheduled: {
    label: "PENDIENTE",
    bg: "rgba(140, 15, 44, 0.08)",
    color: "primary.main",
  },
  finished: {
    label: "FINALIZADO",
    bg: "rgba(25, 118, 210, 0.08)",
    color: "info.main",
  },
  cancelled: {
    label: "CANCELADO",
    bg: "rgba(100, 100, 100, 0.10)",
    color: "text.secondary",
  },
};

function getToken() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return (
      localStorage.getItem("access") ||
      localStorage.getItem("token") ||
      user?.access ||
      user?.token ||
      ""
    );
  } catch {
    return localStorage.getItem("access") || localStorage.getItem("token") || "";
  }
}

function authFetch(url, options = {}) {
  const token = getToken();
  const headers = {
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(url, {
    credentials: "include",
    ...options,
    headers,
  });
}

function formatDateLabel(date, time) {
  const matchDate = new Date(`${date}T12:00:00`);
  return `${matchDate.toLocaleDateString("es-EC", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })} · ${time}`;
}

function TeamScoreCard({
  side,
  teamName,
  logo,
  score,
  disabled,
  busy,
  onDelta,
}) {
  const points = [1, 2, 3];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "22px",
        border: "1px solid rgba(140,15,44,0.12)",
        p: { xs: 1.5, sm: 2 },
        background:
          side === "local"
            ? "linear-gradient(180deg, rgba(140,15,44,0.05) 0%, rgba(140,15,44,0.02) 100%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.015) 100%)",
      }}
    >
      <Stack spacing={1.4} alignItems="center">
        {logo ? (
          <Box
            component="img"
            src={logo}
            alt={teamName}
            sx={{
              width: { xs: 58, sm: 74 },
              height: { xs: 58, sm: 74 },
              objectFit: "contain",
            }}
          />
        ) : (
          <Box
            sx={{
              width: { xs: 58, sm: 74 },
              height: { xs: 58, sm: 74 },
              borderRadius: "18px",
              backgroundColor: "rgba(140,15,44,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SportsBasketballRoundedIcon sx={{ color: "primary.main", fontSize: 32 }} />
          </Box>
        )}

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: ".98rem", sm: "1.06rem" },
            textAlign: "center",
            lineHeight: 1.2,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {teamName}
        </Typography>

        <Box
          sx={{
            minWidth: { xs: 128, sm: 150 },
            px: 2,
            py: 1.2,
            borderRadius: "20px",
            backgroundColor: "#111",
            textAlign: "center",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2.7rem", sm: "3.2rem" },
              lineHeight: 1,
              letterSpacing: ".04em",
              color: "#ff6f00",
            }}
          >
            {score}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          {points.map((value) => (
            <Button
              key={value}
              variant="contained"
              disabled={disabled || busy}
              onClick={() => onDelta(value)}
              sx={{
                minWidth: 62,
                height: 44,
                borderRadius: "12px",
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              +{value}
            </Button>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <Button
            variant="outlined"
            disabled={disabled || busy || score <= 0}
            onClick={() => onDelta(-1)}
            startIcon={<RemoveRoundedIcon />}
            sx={{
              flex: 1,
              height: 42,
              borderRadius: "12px",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            1
          </Button>

          <Button
            variant="outlined"
            disabled={disabled || busy || score <= 1}
            onClick={() => onDelta(-2)}
            startIcon={<RemoveRoundedIcon />}
            sx={{
              flex: 1,
              height: 42,
              borderRadius: "12px",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            2
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

export default function LiveMatchControl() {
  const navigate = useNavigate();
  const { match_id } = useParams();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");

  const loadMatch = async (isRefresh = false) => {
    try {
      if (!isRefresh) {
        setLoading(true);
      }
      setError("");

      const res = await authFetch(`/matches/${match_id}/`);

      if (res.status === 401) {
        throw new Error("Tu sesión ha expirado");
      }

      if (!res.ok) {
        throw new Error("No se pudo cargar el partido");
      }

      const data = await res.json();
      setMatch(data);
    } catch (err) {
      setError(err.message || "No se pudo cargar la información del partido");
    } finally {
      setLoading(false);
      if (isRefresh) {
        setBusyAction("");
      }
    }
  };

  useEffect(() => {
    loadMatch();
  }, [match_id]);

  const statusStyle = useMemo(() => {
    return statusMap[match?.status] || statusMap.scheduled;
  }, [match]);

  const handleDelta = async (team, delta) => {
    try {
      setBusyAction(`score-${team}-${delta}`);
      setError("");

      const previous = match;
      const nextLocal =
        team === "local" ? Math.max(0, (previous.score_local || 0) + delta) : previous.score_local;
      const nextVisitor =
        team === "visitor" ? Math.max(0, (previous.score_visitor || 0) + delta) : previous.score_visitor;

      setMatch((prev) => ({
        ...prev,
        score_local: nextLocal,
        score_visitor: nextVisitor,
      }));

      const res = await authFetch(`/matches/update-score/${match_id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          team,
          delta,
        }),
      });

      if (res.status === 401) {
        throw new Error("Tu sesión ha expirado");
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "No se pudo actualizar el marcador");
      }

      const data = await res.json();
      if (data?.match) {
        setMatch(data.match);
      } else {
        await loadMatch(true);
      }
    } catch (err) {
      await loadMatch(true);
      setError(err.message || "No se pudo actualizar el marcador");
    } finally {
      setBusyAction("");
    }
  };

  const handleStatus = async (endpoint, actionKey) => {
    try {
      setBusyAction(actionKey);
      setError("");

      const res = await authFetch(`/matches/${endpoint}/${match_id}/`, {
        method: "POST",
      });

      if (res.status === 401) {
        throw new Error("Tu sesión ha expirado");
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "No se pudo actualizar el estado");
      }

      const data = await res.json();
      if (data?.match) {
        setMatch(data.match);
      } else {
        await loadMatch(true);
      }
    } catch (err) {
      setError(err.message || "No se pudo actualizar el estado del partido");
    } finally {
      setBusyAction("");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!match) {
    return (
      <Card
        elevation={0}
        sx={{
          maxWidth: 760,
          mx: "auto",
          borderRadius: "22px",
          border: "1px solid rgba(211,47,47,0.16)",
          p: 3,
        }}
      >
        <Typography sx={{ fontWeight: 800, color: "error.main" }}>
          Partido no encontrado
        </Typography>
      </Card>
    );
  }

  const scoreLocked = match.status === "finished" || match.status === "cancelled";

  return (
    <Box sx={{ width: "100%", maxWidth: 980, mx: "auto" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            onClick={() => navigate("/admin")}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              backgroundColor: "#fff",
              border: "1px solid rgba(140,15,44,0.12)",
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: "1.4rem", md: "1.85rem" },
                fontWeight: 800,
                lineHeight: 1.1,
              }}
            >
              Control del partido
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.3 }}>
              Actualiza el marcador y controla el estado del encuentro
            </Typography>
          </Box>
        </Stack>

        <IconButton
          onClick={() => {
            setBusyAction("refresh");
            loadMatch(true);
          }}
          disabled={busyAction === "refresh"}
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            backgroundColor: "#fff",
            border: "1px solid rgba(140,15,44,0.12)",
          }}
        >
          {busyAction === "refresh" ? (
            <CircularProgress size={18} />
          ) : (
            <RefreshRoundedIcon />
          )}
        </IconButton>
      </Stack>

      {error ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: "18px",
            border: "1px solid rgba(211,47,47,0.16)",
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ color: "error.main", fontWeight: 700 }}>{error}</Typography>
        </Card>
      ) : null}

      <Card
        elevation={0}
        sx={{
          borderRadius: "26px",
          border: "1px solid rgba(140,15,44,0.12)",
          p: { xs: 1.5, sm: 2.2, md: 2.5 },
          overflow: "hidden",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Box
              sx={{
                alignSelf: { xs: "flex-start", md: "center" },
                px: 1.2,
                py: 0.55,
                borderRadius: "999px",
                backgroundColor: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: 800,
                fontSize: ".8rem",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {statusStyle.label}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.8, sm: 1.4 }}
              useFlexGap
              flexWrap="wrap"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Stack direction="row" spacing={0.6} alignItems="center">
                <CategoryRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {match.category}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <PlaceRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {match.venue}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {formatDateLabel(match.date, match.time)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <TeamScoreCard
              side="local"
              teamName={match.team_local}
              logo={match.team_local_logo}
              score={match.score_local}
              disabled={scoreLocked}
              busy={busyAction.startsWith("score-")}
              onDelta={(delta) => handleDelta("local", delta)}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: { xs: 0.4, md: 0 },
              }}
            >
              <Box
                sx={{
                  minWidth: 86,
                  px: 2,
                  py: 1.2,
                  borderRadius: "18px",
                  backgroundColor: "rgba(140,15,44,0.06)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "primary.main",
                    fontSize: { xs: "1.1rem", md: "1.2rem" },
                    letterSpacing: ".08em",
                  }}
                >
                  VS
                </Typography>
              </Box>
            </Box>

            <TeamScoreCard
              side="visitor"
              teamName={match.team_visitor}
              logo={match.team_visitor_logo}
              score={match.score_visitor}
              disabled={scoreLocked}
              busy={busyAction.startsWith("score-")}
              onDelta={(delta) => handleDelta("visitor", delta)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.2,
            }}
          >
            <Button
              variant="contained"
              color="error"
              disabled={busyAction !== "" || match.status === "live" || match.status === "finished" || match.status === "cancelled"}
              onClick={() => handleStatus("start-live", "start")}
              startIcon={<PlayArrowRoundedIcon />}
              sx={{
                height: 48,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Iniciar en vivo
            </Button>

            <Button
              variant="outlined"
              disabled={busyAction !== "" || match.status !== "live"}
              onClick={() => handleStatus("pause-live", "pause")}
              startIcon={<PauseRoundedIcon />}
              sx={{
                height: 48,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Pausar partido
            </Button>

            <Button
              variant="outlined"
              color="success"
              disabled={busyAction !== "" || match.status === "finished" || match.status === "cancelled"}
              onClick={() => handleStatus("finish-live", "finish")}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{
                height: 48,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Finalizar partido
            </Button>
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              fontSize: ".88rem",
              fontWeight: 600,
            }}
          >
            Transmisión en tiempo real
          </Typography>
        </Stack>
      </Card>
    </Box>
  );
}